import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useAlarmState, useRealtime } from "../realtime/RealtimeProvider";
import { alarm } from "../realtime/alarm";
import { errorMessage } from "../api/client";
import type { AlertEvent } from "../api/types";
import { fmtWhen } from "../lib/time";
import { displayTitle } from "../lib/alertTitle";
import { HoloRings } from "../scene/HoloRings";
import { ArrowsInSimpleIcon, CheckIcon, ListIcon, SpeakerHighIcon, SpeakerSlashIcon } from "@phosphor-icons/react";
import { Button, ButtonLink } from "../components/Button";

// Threat takeover for unacknowledged panic alerts (button or in-app), in the
// approved mockup's language: pitch black, the hologram rings turned red, the
// alert title, a white Acknowledge button, and small Silence and Minimize.
// The alarm itself is RealtimeProvider's. Minimized, it is one bar with the
// title and Ack. In the pilot view it always stays a bar so it never covers
// the flight feed.

function detail(e: AlertEvent): string {
  const what = e.body || e.source_name || (e.kind === "button" ? "Button pressed" : "Panic alert");
  return `${what} · ${fmtWhen(e.created_at, true)}`;
}

function useAck() {
  const { activeAlerts, ack } = useRealtime();
  const [acking, setAcking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (e: AlertEvent) => {
    setAcking(e.id);
    setError(null);
    try {
      await ack(e.id);
      if (activeAlerts.length <= 1) document.querySelector<HTMLElement>(".badge")?.focus({ preventScroll: true });
    } catch (err) {
      setError(`ACK failed. ${errorMessage(err)}`);
    } finally {
      setAcking(null);
    }
  };
  return { acking, error, run };
}

function SoundControl() {
  const { silence } = useRealtime();
  const { playing, blocked } = useAlarmState();
  if (!blocked && !playing) return null;
  const label = blocked ? "turn on sound" : "silence";
  const onClick = blocked ? () => void alarm.unlock() : silence;
  return (
    <Button size="sm" variant="ghost" icon={blocked ? SpeakerHighIcon : SpeakerSlashIcon} onClick={onClick}>
      {label}
    </Button>
  );
}

export function ThreatTakeover() {
  const { activeAlerts } = useRealtime();
  const location = useLocation();
  const pilotRoute = /^\/drones\/[^/]+/.test(location.pathname);
  const [minimized, setMinimized] = useState<Set<string>>(() => new Set());
  const { acking, error, run } = useAck();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);

  const top = activeAlerts[0];
  const more = activeAlerts.length - 1;
  const expanded = !!top && !pilotRoute && activeAlerts.some((a) => !minimized.has(a.id));

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (expanded && !d.open) {
      d.showModal();
      // Land on the alert text, not on ACK, so a stray Enter cannot acknowledge it.
      focusRef.current?.focus();
    } else if (!expanded && d.open) {
      d.close();
    }
  }, [expanded]);

  const minimize = () => setMinimized(new Set(activeAlerts.map((a) => a.id)));
  const expand = () => setMinimized(new Set());

  return (
    <>
      <dialog
        ref={dialogRef}
        className="threat"
        aria-labelledby="threat-title"
        aria-describedby="threat-body"
        onCancel={(e) => {
          e.preventDefault();
          minimize();
        }}
      >
        {top && expanded ? (
          <div className="threat__frame">
            <div className="threat__stage">
              <HoloRings variant="threat" idPrefix="threat" />
              <div className="threat__core" ref={focusRef} tabIndex={-1} key={top.id}>
                <p className="threat__kicker">{top.kind === "manual" ? "panic alert" : "button pressed"}</p>
                <h2 id="threat-title" className="threat__title">
                  {displayTitle(top.title)}
                </h2>
                <p id="threat-body" className="threat__body">
                  {detail(top)}
                </p>
              </div>
            </div>
            <div className="threat__actions">
              <Button variant="primary" icon={CheckIcon} className="threat__ack" onClick={() => void run(top)} disabled={acking === top.id}>
                {acking === top.id ? "acknowledging…" : "acknowledge"}
              </Button>
              <div className="threat__secondary">
                <SoundControl />
                <Button size="sm" variant="ghost" icon={ArrowsInSimpleIcon} onClick={minimize}>
                  minimize
                </Button>
                {more > 0 ? (
                  <ButtonLink to="/alerts" state={{ fromScene: true }} size="sm" variant="ghost" icon={ListIcon} onClick={minimize}>
                    {`${more} more`}
                  </ButtonLink>
                ) : null}
              </div>
              {error ? (
                <p className="threat__error" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </dialog>

      {top && !expanded ? (
        <section className={`threat-bar${pilotRoute ? " threat-bar--inline" : ""}`} aria-label="Unacknowledged panic alert">
          <span className="threat-bar__pip" aria-hidden="true" />
          <div className="threat-bar__text" role="alert" key={top.id}>
            {pilotRoute ? (
              <p className="threat-bar__title">{displayTitle(top.title)}</p>
            ) : (
              <button type="button" className="threat-bar__title" onClick={expand} aria-label={`${displayTitle(top.title)}. Show the alert.`}>
                {displayTitle(top.title)}
              </button>
            )}
            {more > 0 ? <span className="threat-bar__more">+{more}</span> : null}
          </div>
          <SoundControl />
          <Button size="sm" variant="primary" icon={CheckIcon} className="threat-bar__ack" onClick={() => void run(top)} disabled={acking === top.id}>
            {acking === top.id ? "…" : "ack"}
          </Button>
          {error ? (
            <p className="threat-bar__error" role="alert">
              {error}
            </p>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
